import {Component, OnDestroy, OnInit} from '@angular/core';
import {Observable} from 'rxjs';
import {IndicationBasicModel} from '@core/modules/rest/indication/response/indication-basic.model';
import {IndicationActions, IndicationReducer} from '../../indication/store';
import {AppState} from '@core/reducers';
import {Store} from '@ngrx/store';
import {ConsentUseCaseBasicModel} from '@core/modules/rest/consent-use-case/response/consent-use-case-basic.model';
import {ConsentUseCaseActions, ConsentUseCaseReducer} from '../../consent-use-case/store';
import {ActivatedRoute, Router} from '@angular/router';
import {SECURED__CONSENT, SECURED__WIZARD} from '@core/routes/app-routes';
import {UnitARequestModel} from '@core/modules/rest/consent/request/unit-a-request.model';
import {ConsentActions, ConsentReducer} from '../../display-consent/store';
import {ProvisionDurationEnum} from '@core/modules/rest/indication/enums/provisionDurationEnum';
import {HeaderInfoModel} from '../model/wizard-header.model';
import {TranslateService} from '@ngx-translate/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {LanguageStyleEnum} from '@core/enums/language-style.enum';
import {AuthRestService} from '@core/modules/rest/authentication/auth-rest.service';
import {ConsentRestService} from '@core/modules/rest/consent/consent-rest.service';
import {LanguageEnum} from '@core/enums/language.enum';
import {ConsentRequestModel} from '@core/modules/rest/consent/request/consent-request.model';
import {PresentationModeEnum} from '@core/enums/presentation-mode.enum';
import {WizardActions, WizardReducer} from '../store';
import {filter, take, tap} from 'rxjs/operators';
import {WELCOME} from '@core/services/routes.service';
import {LoggedUserDataModel} from '@core/modules/rest/user/response/logged-user-data.model';
import {CoreReducer} from '@core/store';
import {UserSettingsModel} from '@core/modules/rest/user/response/user-settings.model';
import {TtsControlEnum} from '@core/enums/tts-control.enum';
import {ReadingSpeedEnum} from '@core/enums/reading-speed.enum';
import {TtsRestService} from '@core/modules/rest/text-to-speech/tts-rest.service';
import {TtsHelperService} from '@shared/utils/tts-helper.service';
import {UserRestService} from '@core/modules/rest/user/user-rest.service';
import {PersonalSettingsModel} from '@shared/model/personal-settings.model';
import {ReadingContentTypeEnum} from '@core/enums/reading-content-type.enum';
import {WizardStepsEnum} from '@core/enums/wizard-steps.enum';
import {WizardHelperService} from '@shared/utils/wizard-helper.service';
import {ConsentUseCaseRestService} from '@core/modules/rest/consent-use-case/consent-use-case-rest.service';
import * as fromCoreActions from '@core/store/core.actions';
import {ChapterUseCaseModel} from '@core/modules/rest/consent/request/chapter-use-case.model';

@UntilDestroy()
@Component({
    selector: 'app-wizard',
    templateUrl: './wizard-container.component.html',
    styleUrls: ['./wizard-container.component.scss']
})
export class WizardContainerComponent implements OnInit, OnDestroy {

    wizardContent$: Observable<string> = this.store.select(WizardReducer.wizardContentSelector);
    indications$: Observable<IndicationBasicModel[]> = this.store.select(IndicationReducer.indicationsSelector);
    consentUseCases$: Observable<ConsentUseCaseBasicModel[]> = this.store.select(ConsentUseCaseReducer.consentUseCaseSelector);
    durationInMillis$: Observable<number> = this.store.select(ConsentReducer.consentDurationSelector);
    loading$: Observable<boolean> = this.store.select(ConsentUseCaseReducer.loadingSelector);
    // loggedUserInfo$: Observable<LoggedUserDataModel> = this.store.select(CoreReducer.loggedUserDataSelector);

    selectedIndicationId?: string;
    userPresetIndicationId?: string;
    selectedUseCaseId?: string;
    consentUseCaseDetails: ConsentUseCaseBasicModel;
    selectedProvisionDuration = null;
    chapterUnits: ChapterUseCaseModel[] = [];
    consentUseCases: ConsentUseCaseBasicModel[] = [];
    headerInfo: HeaderInfoModel = {
        wizardStepHeader: '',
        wizardStepParagraph: ''
    };
    language?: LanguageEnum;
    languageStyle?: LanguageStyleEnum;
    presentationMode?: PresentationModeEnum;
    stepperTexts: string[] = null;
    wizardStepEnum: WizardStepsEnum;
    stepNo: number;
    showHeaderButton = false;
    headerButtonText: string | undefined;
    submittedConsentId: string | undefined;
    useCaseDetailsShow = false;

    customUseCasesVisible = true;
    easyLanguageStyleVisible = false;
    ttsIconState = TtsControlEnum.playState;
    repeatButtonDisabled = true;
    showHeaderControls = true;
    audio = new Audio();
    ttsUrl: string | undefined;
    firstPlay = true;

    textToRead: string | undefined;
    autoPlayVoice = false;

    personalSettings: PersonalSettingsModel | undefined;
    numberOfWizardSteps = 0;

    wizardSteps: WizardStepsEnum[];
    missingData: WizardStepsEnum[] = [];

    wsLanguageStyle = WizardStepsEnum.LANGUAGE_STYLE;
    wsPresentationMode = WizardStepsEnum.PRESENTATION_MODE;
    wsIndications = WizardStepsEnum.INDICATIONS;
    wsUseCases = WizardStepsEnum.USE_CASES;

    languageStyleSet: boolean;
    presentationModeSet: boolean;
    indicationSet: boolean;
    useCaseSet: boolean;

    constructor(
        private store: Store<AppState>,
        private router: Router,
        private route: ActivatedRoute,
        private translate: TranslateService,
        private authService: AuthRestService,
        private consentService: ConsentRestService,
        private ttsService: TtsRestService,
        private ttsHelperService: TtsHelperService,
        private userService: UserRestService,
        private wizardHelper: WizardHelperService,
        private consentUseCaseRestService: ConsentUseCaseRestService) {
    }

    ngOnInit(): void {
        this.wizardSteps = this.wizardHelper.wizardSteps();
        this.store.dispatch({type: '[Core] Wizard page opened'});
        this.audio.addEventListener('ended', this.setButtonTextOnEnd, false);

        this.userService.getLoggedUser().pipe(filter(value => !!value), take(1)).subscribe(loggedUserData => {
            this.personalSettings = JSON.parse(loggedUserData.personalSettings);
            this.userPresetIndicationId = this.personalSettings.indicationId;

            this.route.queryParamMap.subscribe(params => {
                this.submittedConsentId = params.get('consentId') as string;
                this.firstPlay = true;

                if (this.submittedConsentId) {
                    this.store.dispatch(WizardActions.GetWizardContent({consentId: this.submittedConsentId}));
                    this.wizardContent$.pipe(untilDestroyed(this)).subscribe((wizardContent: any) => {
                        if (wizardContent) {

                            this.language = this.personalSettings.language;
                            this.languageStyle = this.personalSettings.languageStyle;
                            this.presentationMode = this.personalSettings.presentationMode;
                            this.selectedIndicationId = this.personalSettings.indicationId;
                            this.selectedUseCaseId = this.personalSettings.useCaseId;

                            this.languageStyleSet = this.languageStyle !== LanguageStyleEnum.CUSTOM;
                            this.presentationModeSet = this.presentationMode !== PresentationModeEnum.CUSTOM;
                            this.indicationSet = this.personalSettings.indicationId !== null;
                            this.useCaseSet = this.personalSettings.useCaseId !== 'CUSTOM_USE_CASE';

                            if (wizardContent.missingData && wizardContent.missingData.length > 0) {
                                this.missingData = wizardContent.missingData;
                            } else {
                                if (!this.languageStyleSet && this.missingData.indexOf(WizardStepsEnum.LANGUAGE_STYLE) < 0) {
                                    this.missingData.push(WizardStepsEnum.LANGUAGE_STYLE);
                                }
                                if (!this.presentationModeSet && this.missingData.indexOf(WizardStepsEnum.PRESENTATION_MODE) < 0) {
                                    this.missingData.push(WizardStepsEnum.PRESENTATION_MODE);
                                }
                                if (!this.indicationSet && this.missingData.indexOf(WizardStepsEnum.INDICATIONS) < 0) {
                                    this.missingData.push(WizardStepsEnum.INDICATIONS);
                                }
                                if (!this.useCaseSet && this.missingData.indexOf(WizardStepsEnum.USE_CASES) < 0) {
                                    this.missingData.push(WizardStepsEnum.USE_CASES);
                                }
                            }

                            this.numberOfWizardSteps = this.missingData.length;
                            this.wizardStepEnum = wizardContent.wizardStepEnum;
                            this.stepNo = this.getMissingStepNumberOfWizardStep(this.wizardStepEnum);
                            this.previewChosenWizardStepScreen(this.wizardStepEnum);
                            this.setWizardStepperTexts(this.missingData);

                            if (this.autoPlayVoice && this.showHeaderControls) {
                                this.readText();
                            }
                        }
                    });
                } else {
                    this.router.navigate([WELCOME]);
                }
            });

            const settings: UserSettingsModel = JSON.parse(loggedUserData.settings);
            this.customUseCasesVisible = !settings.customUseCaseOff;
            this.easyLanguageStyleVisible = !settings.lightLanguageStyleOff;
        });

        sessionStorage.getItem('audioMode') ? this.autoPlayVoice = true : this.autoPlayVoice = false;
    }

    getMissingStepNumberOfWizardStep(wizardStepEnum: WizardStepsEnum): number {
        return this.missingData.indexOf(wizardStepEnum) + 1;
    }

    previewChosenWizardStepScreen(wizardStepEnum: WizardStepsEnum): void {
        switch (wizardStepEnum) {
            case WizardStepsEnum.LANGUAGE_STYLE:
                this.showLanguageStyle();
                break;
            case WizardStepsEnum.PRESENTATION_MODE:
                this.showPresentationMode();
                break;
            case WizardStepsEnum.INDICATIONS:
                this.showIndications();
                break;
            case WizardStepsEnum.USE_CASES:
                if (!this.useCaseDetailsShow) {
                    this.showUseCases();
                } else {
                    this.showCustomUseCase();
                }
                break;
        }
    }

    setWizardStepperTexts(missingData: WizardStepsEnum[]): void {

        this.stepperTexts = [];
        missingData.forEach(item => {
            switch (item) {
                case WizardStepsEnum.LANGUAGE_STYLE:
                    this.stepperTexts.push(this.translate.instant('ONBOARDING.LANGUAGE_STYLE.TITLE'));
                    break;
                case WizardStepsEnum.PRESENTATION_MODE:
                    this.stepperTexts.push(this.translate.instant('ONBOARDING.PRESENTATION_MODE.TITLE'));
                    break;
                case WizardStepsEnum.INDICATIONS:
                    this.stepperTexts.push(this.translate.instant('ONBOARDING.EDUCATION.TITLE'));
                    break;
                case WizardStepsEnum.USE_CASES:
                    this.stepperTexts.push(this.translate.instant('ONBOARDING.CONSENT_USE_CASE.TITLE'));
                    break;
            }
        });
    }


    languageStyleSelected(languageStyle: LanguageStyleEnum): void {
        this.languageStyle = languageStyle;
        this.saveOnboardingSettings();
        this.nextStep();
    }

    presentationModeSelected(presentationMode: PresentationModeEnum): void {
        if (presentationMode === PresentationModeEnum.TEXT && sessionStorage.getItem('audioMode')) {
            sessionStorage.removeItem('audioMode');
        }

        this.presentationMode = presentationMode;
        this.presentationModeSet = true;
        this.saveOnboardingSettings();
        this.nextStep();
    }

    indicationSelected(id: string): void {
        this.selectedIndicationId = id;
        this.indicationSet = true;
        this.saveSelectedIndication();
        this.nextStep();
    }

    indicationFullSelected(indication: IndicationBasicModel): void {
        this.indicationSelected(indication.id);
    }

    useCaseSelected(useCase: ConsentUseCaseBasicModel): void {
        this.useCaseDetailsShow = false;
        this.consentUseCaseDetails = useCase;

        this.selectedUseCaseId = useCase.id;
        this.useCaseSet = true;
        this.saveSelectedUseCase();
        this.nextStep();
    }

    customUseCaseSelected(customUseCase: ConsentUseCaseBasicModel): void {
        this.useCaseDetailsShow = true;
        this.consentUseCaseDetails = customUseCase;
        this.selectedUseCaseId = this.consentUseCaseDetails.id;
        this.nextStep();
    }

    useCaseDetailsSelected(): void {
        this.finalStep();
    }

    saveOnboardingSettings(): void {
        this.personalSettings = {
            ...this.personalSettings,
            language: this.language,
            languageStyle: this.languageStyle,
            presentationMode: this.presentationMode,
        };

        this.store.dispatch(fromCoreActions.UpdateUserPersonalSettings({settings: JSON.stringify(this.personalSettings)}));
    }

    saveSelectedIndication(): void {
        this.personalSettings = {
            ...this.personalSettings,
            indicationId: this.selectedIndicationId
        };
        this.userService.updatePersonalSettings(JSON.stringify(this.personalSettings)).subscribe();
    }

    saveSelectedUseCase(): void {
        this.personalSettings = {
            ...this.personalSettings,
            useCaseId: this.selectedUseCaseId
        };
        this.userService.updatePersonalSettings(JSON.stringify(this.personalSettings)).subscribe();
    }


    nextStep(): void {
        const currentItemThatIsMissing = this.missingData.indexOf(this.wizardStepEnum);
        if (this.missingData.length < currentItemThatIsMissing + 2 && !this.useCaseDetailsShow) {
            this.consentUseCaseRestService.getConsentUseCases(this.selectedIndicationId, this.personalSettings.language,
                this.personalSettings.languageStyle).subscribe((consentUseCases: ConsentUseCaseBasicModel[]) => {
                this.consentUseCases = consentUseCases;
                if (this.selectedUseCaseId && this.consentUseCases.length > 0) {
                    this.getUseCaseDetailsFromUseCase(this.selectedUseCaseId);
                }
                this.finalStep();
            });

        } else {
            if (this.wizardStepEnum !== WizardStepsEnum.USE_CASES && !this.useCaseDetailsShow) {
                this.wizardStepEnum = this.missingData[currentItemThatIsMissing + 1];
                this.stepNo = this.getMissingStepNumberOfWizardStep(this.wizardStepEnum);
            } else {
                this.showCustomUseCase();
            }
            this.pause(false);
            this.saveProgress(false);
        }
    }

    stepBackButtonEvent(): void {
        this.pause(false);
        if (this.wizardStepEnum === WizardStepsEnum.USE_CASES && this.consentUseCaseDetails) {
            this.consentUseCaseDetails = undefined;
        } else {
            const location = this.missingData.indexOf(this.wizardStepEnum);
            this.wizardStepEnum = this.missingData[location - 1];
            this.stepNo = this.getMissingStepNumberOfWizardStep(this.wizardStepEnum);
        }
        if (this.wizardStepEnum !== undefined) {
            switch (this.wizardStepEnum) {
                case WizardStepsEnum.LANGUAGE_STYLE:
                    this.languageStyle = LanguageStyleEnum.CUSTOM;
                    this.saveOnboardingSettings();
                    this.showLanguageStyle();
                    break;
                case WizardStepsEnum.PRESENTATION_MODE:
                    this.presentationMode = PresentationModeEnum.CUSTOM;
                    this.saveOnboardingSettings();
                    this.showPresentationMode();
                    break;
                case WizardStepsEnum.INDICATIONS:
                    this.selectedIndicationId = null;
                    this.saveSelectedIndication();
                    this.showIndications();
                    break;
                case WizardStepsEnum.USE_CASES:
                    this.selectedUseCaseId = null;
                    this.saveSelectedUseCase();
                    this.showUseCases();
                    break;
            }
        } else {
            this.router.navigate([WELCOME]);
        }
    }

    onLogOut(): void {
        this.authService.logout();
    }

    finalStep(): void {
        this.saveProgress(true);
    }

    chapterUseCaseChanged(event: UnitARequestModel): void {
        if (!this.selectedProvisionDuration) {
            this.selectedProvisionDuration = event.provisionDuration;
        }
        this.chapterUnits = this.chapterUnits
            .map((item: ChapterUseCaseModel) => {
                return {
                    unitId: item.unitId,
                    unitLevel: event.unitId === item.unitId ? event.unitLevel : item.unitLevel,
                    unitTitle: item.unitTitle
                };
            });
        this.updateDuration();
    }

    provisionDurationChanged(provisionDuration: ProvisionDurationEnum): void {
        this.selectedProvisionDuration = provisionDuration;
        this.updateChapterMapAndRelevance(this.selectedProvisionDuration);
        this.updateDuration();
    }

    updateDuration(): void {
        const consent: ConsentRequestModel = {
            indicationId: this.selectedIndicationId,
            language: this.language,
            languageStyle: this.languageStyle,
            chapterUnits: this.chapterUnits.map(item => {
                return {
                    unitId: item.unitId,
                    unitLevel: item.unitLevel,
                    provisionDuration: this.selectedProvisionDuration,
                };

            }),
            submittedConsentId: this.submittedConsentId
        };
        this.store.dispatch(ConsentActions.GetConsentDuration({consent}));
    }

    getUseCasesForIndication(indicationId: string, language: LanguageEnum, languageStyle: LanguageStyleEnum): void {
        if (indicationId !== '' && languageStyle !== undefined) {
            this.store.dispatch(ConsentUseCaseActions.GetConsentUseCases({indicationId, language, languageStyle}));
        }
    }

    showLanguageStyle(): void {
        this.showHeaderControls = true;
        this.showHeaderButton = false;
        this.prepareTextToReadForLanguageStyle();
        this.headerInfo = {
            wizardStepHeader: this.translate.instant('ONBOARDING.LANGUAGE_STYLE.TITLE'),
            wizardStepParagraph: this.translate.instant('ONBOARDING.LANGUAGE_STYLE.DESCRIPTION')
        };
    }

    showPresentationMode(): void {
        this.showHeaderControls = false;
        this.showHeaderButton = false;
        this.headerInfo = {
            wizardStepHeader: this.translate.instant('ONBOARDING.PRESENTATION_MODE.TITLE'),
            wizardStepParagraph: this.translate.instant('ONBOARDING.PRESENTATION_MODE.DESCRIPTION')
        };
    }

    showIndications(): void {
        this.showHeaderControls = false;
        this.prepareDataUntilIndicationsStep();
        this.showHeaderButton = false;
        this.headerInfo = {
            wizardStepHeader: this.translate.instant('ONBOARDING.EDUCATION.TITLE'),
            wizardStepParagraph: this.translate.instant('ONBOARDING.EDUCATION.DESCRIPTION')
        };
    }

    showUseCases(): void {
        this.showHeaderControls = false;
        this.prepareDataUntilUseCaseStep();
        this.showHeaderButton = false;
        this.useCaseDetailsShow = false;
        this.headerInfo = {
            wizardStepHeader: this.translate.instant('ONBOARDING.CONSENT_USE_CASE.TITLE'),
            wizardStepParagraph: this.translate.instant('ONBOARDING.CONSENT_USE_CASE.DESCRIPTION')
        };
    }

    showCustomUseCase(): void {
        this.chapterUnits = this.consentUseCaseDetails?.chapterUnits
            .map((item => {
                return {
                    unitId: item.unitId,
                    unitLevel: item.unitLevel,
                    unitTitle: item.unitTitle
                };
            }));

        this.useCaseDetailsShow = true;
        this.showHeaderControls = false;
        this.headerInfo = {
            wizardStepHeader: this.translate.instant('ONBOARDING.USE_CASE_LEVELS.TITLE'),
            wizardStepParagraph: this.translate.instant('ONBOARDING.USE_CASE_LEVELS.DESCRIPTION')
        };
    }

    private prepareDataUntilIndicationsStep(): void {
        this.store.dispatch(IndicationActions.GetIndications({language: this.language}));
    }

    private prepareDataUntilUseCaseStep(): void {
        this.prepareDataUntilIndicationsStep();
        this.getUseCasesForIndication(this.selectedIndicationId, this.language, this.languageStyle);
    }

    updateChapterMapAndRelevance(provisionDuration: ProvisionDurationEnum): void {
        this.chapterUnits = this.chapterUnits.map(item => {
            return {
                ...item,
                provisionDuration
            };
        });
    }

    saveProgress(consentStarted: boolean): void {
        let chapterUnits: ChapterUseCaseModel[] = [];
        if (this.chapterUnits && this.chapterUnits.length > 0) {
            chapterUnits = this.chapterUnits;
        }

        const wizContent = {
            wizardStepEnum: this.wizardStepEnum,
            language: this.language,
            languageStyle: this.languageStyle,
            presentationMode: this.presentationMode,
            indicationId: this.selectedIndicationId,
            useCaseId: this.selectedUseCaseId,
            useCaseName: this.consentUseCaseDetails ? this.consentUseCaseDetails.title : '',
            durationInMillis: this.consentUseCaseDetails ? this.consentUseCaseDetails.durationInMilliseconds : 0,
            chapterUnits,
            provisionDuration: this.selectedProvisionDuration,
            missingData: this.missingData
        };

        const request = {
            id: this.submittedConsentId,
            indicationId: this.selectedIndicationId,
            content: null,
            wizardContent: JSON.stringify(wizContent),
            readingTime: 0,
            consentStarted,
            consentUpdated: false,
            languageEnum: this.language
        };

        this.consentService.submitConsent(request).pipe(take(1)).subscribe(() => {
            this.store.dispatch(WizardActions.SetWizardContent({wizardContent: wizContent}));
            const routeToNavigate = consentStarted ? SECURED__CONSENT : SECURED__WIZARD;
            this.router.navigate([routeToNavigate], {
                queryParams: {
                    consentId: this.submittedConsentId,
                }
            });
        });

    }

    getUseCaseDetailsFromUseCase(useCaseId: string): void {
        this.consentUseCaseDetails = this.consentUseCases.find(x => x.id === useCaseId);
        if (this.consentUseCaseDetails) {
            this.selectedProvisionDuration = this.consentUseCaseDetails.provisionDuration;
            this.chapterUnits = this.consentUseCaseDetails.chapterUnits
                .map((item => {
                    return {
                        unitId: item.unitId,
                        unitLevel: item.unitLevel,
                        unitTitle: item.unitTitle
                    };
                }));
        }
    }

    readText(): void {
        if (this.firstPlay) {
            this.ttsIconState = TtsControlEnum.pauseState;
            this.convertTextToSpeech();
        } else {
            if (this.audio.ended) {
                this.play();
            } else {
                if (this.ttsIconState === TtsControlEnum.playState) {
                    this.resume(false);
                } else {
                    this.pause(false);
                }
            }
        }
    }

    play(): void {
        this.audio.load();
        this.audio.play().then(() => this.ttsIconState = TtsControlEnum.pauseState);
    }

    resume(automatic: boolean): void {
        if (!this.audio.ended) {
            if (this.ttsIconState === TtsControlEnum.pauseState && automatic) {
                this.audio.play().then(() => this.ttsIconState = TtsControlEnum.pauseState);
            }
            if (this.ttsIconState === TtsControlEnum.playState && !automatic) {
                this.audio.play().then(() => this.ttsIconState = TtsControlEnum.pauseState);
            }
        }
    }

    pause(automatic: boolean): void {
        if (!automatic) {
            this.ttsIconState = TtsControlEnum.playState;
        }
        if (!this.audio.ended) {
            this.audio.pause();
        }
    }

    convertTextToSpeech(): void {
        const finalText = this.ttsHelperService.removeSSML(this.textToRead);

        const pollyObject = {
            text: finalText,
            readingSpeed: ReadingSpeedEnum.MEDIUM,
            readingLanguage: this.language,
            readingContentType: ReadingContentTypeEnum.TEXT
        };

        this.firstPlay = false;
        this.repeatButtonDisabled = false;
        this.ttsService.getTTS(pollyObject).subscribe((data: any) => {
            this.ttsUrl = data;
            if (this.ttsUrl) {
                this.audio.src = this.ttsUrl;
                this.play();
            }
        });
    }

    prepareTextToReadForLanguageStyle(): void {
        this.textToRead = this.translate.instant('ONBOARDING.LANGUAGE_STYLE.DESCRIPTION') + '. ' +
            this.translate.instant('VOICE_OUTPUT_TEXTS.LANGUAGE_STYLE_TAB_OPTIONS') +
            this.translate.instant('ONBOARDING.LANGUAGE_STYLE.STANDARD_DESCRIPTION') +
            this.translate.instant('VOICE_OUTPUT_TEXTS.LANGUAGE_STYLE_SELECTION');
    }

    ngOnDestroy(): void {
        this.pause(false);
    }

    setButtonTextOnEnd = (evt: { type: string; }) => {
        if (evt.type === 'ended') {
            this.ttsIconState = TtsControlEnum.playState;
        }
    }

}
